import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Instagram, Mail, MessageCircle, Loader, Calendar, User, Phone, Music, FileText } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
const BookingForm = () => {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !successMessage) {
    return <div className="p-8 border border-primary/20 bg-primary/5 text-center">
        <h3 className="text-2xl font-serif text-primary mb-4">Request Received</h3>
        <p className="text-muted-foreground mb-6">We have received your booking inquiry. We will be in touch shortly.</p>
        <Button onClick={resetForm} variant="outline" className="border-primary text-primary hover:bg-primary hover:text-black">
          Send Another Request
        </Button>
      </div>;
  }
  if (isSubmitted && successMessage) {
    return <div className="p-8 border border-primary/20 bg-primary/5 text-center">
        <h3 className="text-2xl font-serif text-primary mb-4">Request Received</h3>
        <p className="text-muted-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-primary text-primary hover:bg-primary hover:text-black">
          Send Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="flex items-center gap-2 text-sm leading-none font-medium select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50 data-[error=true]:text-destructive !text-[hsl(50,98%,64%)]">Your Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className="bg-background border-input focus:border-primary rounded-none h-12" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="flex items-center gap-2 text-sm leading-none font-medium select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50 data-[error=true]:text-destructive !text-[hsl(50,98%,64%)]">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="bg-background border-input focus:border-primary rounded-none h-12" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="flex items-center gap-2 text-sm leading-none font-medium select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50 data-[error=true]:text-destructive !text-[hsl(50,98%,64%)]">Phone / WhatsApp</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 555 123 4567" className="bg-background border-input focus:border-primary rounded-none h-12" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="event_date" rules={{
        required: "Event date is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="flex items-center gap-2 text-sm leading-none font-medium select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50 data-[error=true]:text-destructive !text-[hsl(50,98%,64%)]">Event Date</FormLabel>
              <FormControl>
                <Input type="date" className="flex w-full min-w-0 border px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive bg-background border-input rounded-none h-12 file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:aria-invalid:ring-destructive/40 focus:border-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="event_type" rules={{
        required: "Event type is required"
      }} render={({
        field
      }) => <FormItem className="md:col-span-2">
              <FormLabel className="flex items-center gap-2 text-sm leading-none font-medium select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50 data-[error=true]:text-destructive !text-[hsl(50,98%,64%)]">Event Type</FormLabel>
              <FormControl>
                <select className="flex h-12 w-full items-center justify-between rounded-none border border-input bg-background px-3 py-2 text-sm ring-offset-background !text-[hsl(50,98%,64%)] placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-50" {...field}>
                  <option value="" disabled>Select event type</option>
                  {["Wedding", "Corporate", "Club Night", "Private Party", "Festival"].map(option => <option key={option} value={option}>
                      {option}
                    </option>)}
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message" render={({
        field
      }) => <FormItem className="md:col-span-2">
              <FormLabel className="flex items-center gap-2 text-sm leading-none font-medium select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50 data-[error=true]:text-destructive !text-[hsl(50,98%,64%)]">Additional Details</FormLabel>
              <FormControl>
                <Textarea placeholder="Any special requests or details..." className="bg-background border-input focus:border-primary rounded-none min-h-[120px]" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="mt-6 p-4 bg-destructive/10 border border-destructive/20 text-destructive text-sm">
          {submitError}
        </div>}

      <div className="mt-8 flex justify-center">
        <Button type="submit" disabled={isSubmitting} className="bg-primary inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive shadow-xs px-4 py-2 has-[>svg]:px-3 w-full min-w-[200px] h-14 text-lg font-serif uppercase tracking-widest rounded-none transition-colors !bg-[hsl(50,98%,64%)] !text-[hsl(0,0%,0%)] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 md:w-auto hover:bg-white hover:text-black">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              Processing...
            </> : 'Confirm Booking'}
        </Button>
      </div>
    </Form>;
};
export default function DirectCommand() {
  const contactMethods = [{
    label: "WhatsApp",
    icon: <MessageCircle className="size-10 md:size-12" />,
    link: "https://wa.me/9820970096",
    text: "9820970096"
  }, {
    label: "Instagram",
    icon: <Instagram className="size-10 md:size-12" />,
    link: "https://instagram.com/djtaurx",
    text: "@djtaurx"
  }, {
    label: "Email",
    icon: <Mail className="size-10 md:size-12" />,
    link: "mailto:info@djtaurx.com",
    text: "info@djtaurx.com"
  }];
  return <section id="direct-command" className="dark bg-background text-foreground py-24 md:py-32 px-4 relative overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-20 bg-[radial-gradient(circle_at_center,_var(--color-primary)_0%,_transparent_40%)] blur-[100px] translate-y-1/2" />

      <div className="max-w-7xl mx-auto relative z-10">
        {}
        <div className="text-center mb-20 md:mb-28">
          <h2 className="font-serif text-5xl font-bold tracking-tighter uppercase mb-4 !text-[hsl(50,98%,64%)] md:text-7xl lg:text-8xl">
            Direct Command
          </h2>
          <div className="w-24 h-1 mx-auto !text-[hsl(50,98%,64%)] !bg-[hsl(50,98%,64%)]" />
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-32">
          {contactMethods.map((method, i) => <Link key={i} to={method.link} newTab={true} className="group block h-full" data-index={i}>
              <div className="h-64 w-full border border-white flex flex-col items-center justify-center gap-6 transition-all duration-300 group-hover:bg-primary group-hover:border-primary cursor-pointer !bg-[hsl(0,0%,0%)] !shadow-[0px_28px_56px_rgba(20,21,31,0.42),_0px_14px_28px_rgba(20,21,31,0.26)] md:h-80">
                <div className="text-white group-hover:text-black transition-colors duration-300">
                  {method.icon}
                </div>
                <div className="text-center">
                  <h3 className="font-serif text-2xl font-bold group-hover:text-black transition-colors duration-300 mb-2 !bg-transparent !text-[hsl(50,98%,64%)] md:text-3xl">
                    {method.label}
                  </h3>
                  <p className="font-sans text-sm tracking-widest uppercase group-hover:text-black/70 transition-colors duration-300 !text-[hsl(50,98%,64%)] md:text-base">
                    {method.text}
                  </p>
                </div>
              </div>
            </Link>)}
        </div>

        {}
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h3 className="font-serif text-3xl mb-4 !text-[hsl(50,98%,64%)] md:text-4xl">Book a DJ</h3>
            <p className="text-muted-foreground max-w-lg mx-auto">
              Ready to dominate your event? Fill out the details below to secure your date.
            </p>
          </div>

          <div className="bg-card/30 border border-white/10 p-6 md:p-10 backdrop-blur-sm">
            <FormProvider formId="booking_form_home" sectionName="direct-command" defaultValues={{
            name: "",
            email: "",
            phone: "",
            event_date: "",
            event_type: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true
            },
            event_date: {
              required: true
            },
            event_type: {
              required: true
            }
          }} submitText="Confirm Booking" successMessage="Your booking request has been received. We will contact you shortly to confirm details.">
              <BookingForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}